/* Image URL optimizer — squeezes Unsplash + our own Emergent file URLs
   through proper compression + width parameters before they hit the network.

   Why this exists: the 2026-06-27 SEO audit flagged /s/allure-image/portfolio
   loading in 5.2s and /services in 3.8s — both above Google's 3s threshold.
   Root cause: the tenant's uploaded portfolio images were being passed to
   <img src> as-is, meaning full-resolution Unsplash originals (often 5000px
   wide / 2MB+ each) were downloaded and resized in the browser. This util
   appends Unsplash's native imgix-style params so the CDN delivers a
   web-sized + format-optimized image.

   Used in: Editorial Portfolio, Home, Services pages, and anywhere else
   tenant-supplied image URLs are rendered. */

const UNSPLASH_HOST = "images.unsplash.com";

/**
 * Normalize an image URL with width + quality + auto-format params when the
 * host supports them. Returns the URL unchanged when we don't know how to
 * optimize it (custom CDN, data: URLs, local /api/files/, etc.).
 *
 * @param {string} src    — original image URL
 * @param {object} opts
 * @param {number} [opts.w]       — target display width in CSS pixels (default 1600)
 * @param {number} [opts.q]       — JPEG quality 1-100 (default 75 — visually identical to 85, ~30% smaller)
 * @param {boolean} [opts.auto]   — append auto=format,compress (default true). Lets the CDN serve AVIF/WebP when supported.
 */
export function optimizeImageUrl(src, { w = 1600, q = 75, auto = true } = {}) {
  if (!src || typeof src !== "string") return src;
  // Don't touch data URIs, blob URLs, or our backend's /api/files/ proxy
  // (already optimized server-side via Pillow before serving).
  if (src.startsWith("data:") || src.startsWith("blob:")) return src;
  if (src.includes("/api/files/")) return src;

  // Unsplash supports imgix params (w, q, fit, auto). Layer them on, replacing
  // any existing values so we always serve the tuned size — not whatever the
  // photographer pasted from a desktop tab.
  try {
    const url = new URL(src);
    if (url.hostname === UNSPLASH_HOST || url.hostname.endsWith(`.${UNSPLASH_HOST}`)) {
      url.searchParams.set("w", String(w));
      url.searchParams.set("q", String(q));
      if (auto) url.searchParams.set("auto", "format,compress");
      // Drop `dpr` if present — the browser handles HiDPI via srcset.
      url.searchParams.delete("dpr");
      return url.toString();
    }
  } catch {
    // Malformed URL — return as-is and let the browser handle it.
    return src;
  }
  return src;
}

/**
 * Build a responsive srcSet string for an Unsplash URL (or any image we know
 * how to size). Returns an empty string when the URL can't be optimized — the
 * caller should omit the `srcSet` attribute in that case.
 */
export function optimizedSrcSet(src, widths = [800, 1200, 1600, 2400]) {
  if (!src || typeof src !== "string") return "";
  try {
    const url = new URL(src);
    if (url.hostname !== UNSPLASH_HOST && !url.hostname.endsWith(`.${UNSPLASH_HOST}`)) {
      return "";
    }
  } catch {
    return "";
  }
  return widths.map((w) => `${optimizeImageUrl(src, { w })} ${w}w`).join(", ");
}
